const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-toast-bg: var(--ctl-color-surface);
    --ctl-toast-text: var(--ctl-color-text);
    --ctl-toast-border: var(--ctl-color-border);
    --ctl-toast-shadow: var(--ctl-shadow-lg);
    --ctl-toast-radius: var(--ctl-radius-12);

    display: block;
    position: relative;
    min-width: calc(var(--ctl-space-7) * 5.83);
    max-width: calc(var(--ctl-space-7) * 8.75);
    background: var(--ctl-toast-bg);
    color: var(--ctl-toast-text);
    border: var(--ctl-border-width-thin) solid var(--ctl-toast-border);
    border-radius: var(--ctl-toast-radius);
    box-shadow: var(--ctl-toast-shadow);
    padding: var(--ctl-space-3-5) var(--ctl-space-4);
    font: var(--ctl-font-weight-normal) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    line-height: var(--ctl-leading-normal);
  }

  /* Intent colors */
  :host([intent="success"]) {
    --ctl-toast-bg: var(--ctl-color-success-surface);
    --ctl-toast-text: var(--ctl-color-success-text);
    --ctl-toast-border: var(--ctl-color-success-border);
  }
  :host([intent="error"]) {
    --ctl-toast-bg: var(--ctl-color-danger-surface);
    --ctl-toast-text: var(--ctl-color-danger-text);
    --ctl-toast-border: var(--ctl-color-danger-border);
  }
  :host([intent="warning"]) {
    --ctl-toast-bg: var(--ctl-color-warning-surface);
    --ctl-toast-text: var(--ctl-color-warning-text);
    --ctl-toast-border: var(--ctl-color-warning-border);
  }
  :host([intent="info"]) {
    --ctl-toast-bg: var(--ctl-color-info-surface);
    --ctl-toast-text: var(--ctl-color-info-text);
    --ctl-toast-border: var(--ctl-color-info-border);
  }

  .toast-title {
    font: var(--ctl-font-weight-semibold) var(--ctl-font-size-lg) var(--ctl-font-sans, system-ui);
    margin: 0 0 var(--ctl-space-1-5);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
  }

  .toast-message {
    display: block;
  }

  .toast-actions {
    display: flex;
    gap: var(--ctl-space-2);
    margin-top: var(--ctl-space-3);
  }

  .close-btn {
    position: absolute;
    top: var(--ctl-space-2-5);
    right: var(--ctl-space-2-5);
    background: transparent;
    border: none;
    cursor: pointer;
    padding: var(--ctl-space-1);
    line-height: 1;
    opacity: var(--ctl-opacity-muted);
    transition: opacity var(--ctl-transition-fast);
    color: currentColor;
    font-size: var(--ctl-font-size-xl);
  }

  .close-btn:hover {
    opacity: 1;
  }

  :host(:not([closable])) .close-btn {
    display: none;
  }
</style>

<div class="toast-container">
  <button class="close-btn" aria-label="Close" part="close-button">×</button>
  <div class="toast-title" part="title">
    <slot name="title"></slot>
  </div>
  <div class="toast-message" part="message">
    <slot></slot>
  </div>
  <div class="toast-actions" part="actions">
    <slot name="actions"></slot>
  </div>
</div>
`;

export class CtlToast extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._autoCloseTimer = null;
  }

  static get observedAttributes() {
    return ["open", "duration", "closable"];
  }

  connectedCallback() {
    const closeBtn = this.shadowRoot.querySelector(".close-btn");
    closeBtn.addEventListener("click", () => this.close("user"));

    // Start auto-close timer if duration is set
    this._startAutoClose();
  }

  disconnectedCallback() {
    this._clearAutoClose();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "duration" && oldValue !== newValue) {
      this._clearAutoClose();
      if (this.hasAttribute("open")) {
        this._startAutoClose();
      }
    } else if (name === "open") {
      if (newValue !== null) {
        this._startAutoClose();
      } else {
        this._clearAutoClose();
      }
    }
  }

  _startAutoClose() {
    this._clearAutoClose();
    const duration = parseInt(this.getAttribute("duration")) || 0;
    if (duration > 0) {
      this._autoCloseTimer = setTimeout(() => {
        this.close("timeout");
      }, duration);
    }
  }

  _clearAutoClose() {
    if (this._autoCloseTimer) {
      clearTimeout(this._autoCloseTimer);
      this._autoCloseTimer = null;
    }
  }

  close(reason = "user") {
    this.removeAttribute("open");
    this.dispatchEvent(
      new CustomEvent("ctl-toast-close", {
        bubbles: true,
        composed: true,
        detail: { reason }
      })
    );
  }
}

customElements.define("ctl-toast", CtlToast);
